// services/locationService.js

import { Geolocation } from '@capacitor/geolocation';
import { PlatformConfig, isCapacitor } from '../utils/platform';

/**
 * Service for handling geolocation functionality with Capacitor and web support
 * Automatically uses Capacitor Geolocation on mobile or HTML5 geolocation on web
 * Provides methods to capture user location with proper error handling
 * and privacy-conscious design
 */
class LocationService {
  /**
   * Get current position using Capacitor (mobile) or HTML5 geolocation (web)
   * @param {Object} options - Geolocation options
   * @returns {Promise<Object>} Location data with coordinates, source, accuracy, and timestamp
   */
  async getCurrentPosition(options = {}) {
    const defaultOptions = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 300000 // 5 minutes
    };
    
    const finalOptions = { ...defaultOptions, ...options };
    
    if (PlatformConfig.useCapacitorGeolocation) {
      // Use Capacitor Geolocation for mobile apps
      try {
        const position = await Geolocation.getCurrentPosition({
          enableHighAccuracy: finalOptions.enableHighAccuracy,
          timeout: finalOptions.timeout,
          maximumAge: finalOptions.maximumAge
        });
        
        return {
          coordinates: {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          },
          source: 'gps',
          accuracy: position.coords.accuracy,
          timestamp: new Date().toISOString()
        };
      } catch (error) {
        throw this._mapCapacitorError(error);
      }
    } else {
      // Use HTML5 Geolocation for web browsers
      return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error('Geolocation not supported'));
          return;
        }
        
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({
              coordinates: {
                lat: position.coords.latitude,
                lng: position.coords.longitude
              },
              source: 'gps',
              accuracy: position.coords.accuracy,
              timestamp: new Date().toISOString()
            });
          },
          (error) => reject(error),
          finalOptions
        );
      });
    }
  }
  
  /**
   * Map Capacitor geolocation errors to standard GeolocationPositionError format
   * @param {Object} capacitorError - Capacitor error object
   * @returns {Error} Formatted error object
   */
  _mapCapacitorError(capacitorError) {
    const error = new Error(capacitorError.message || 'Location error');
    
    // Map Capacitor error codes to standard GeolocationPositionError codes
    switch (capacitorError.message) {
      case 'Location services are not enabled':
      case 'User denied access to location':
        error.code = 1; // PERMISSION_DENIED
        break;
      case 'Unable to retrieve location':
        error.code = 2; // POSITION_UNAVAILABLE
        break;
      case 'Location request timed out':
        error.code = 3; // TIMEOUT
        break;
      default:
        error.code = 2; // POSITION_UNAVAILABLE as default
        break;
    }
    
    return error;
  }
  
  /**
   * Get user-friendly error message for geolocation errors
   * @param {GeolocationPositionError|Error} error - Geolocation error object
   * @returns {string} User-friendly error message
   */
  getErrorMessage(error) {
    const code = error.code || 0;
    
    switch (code) {
      case 1: // PERMISSION_DENIED
        return isCapacitor() 
          ? "Location permission denied. Please enable location access in your device settings."
          : "Location access denied. You can enable it in your browser settings.";
      case 2: // POSITION_UNAVAILABLE
        return "Location information unavailable. Please try again.";
      case 3: // TIMEOUT
        return "Location request timed out. Please try again.";
      default:
        return "Failed to get location. Please try again.";
    }
  }

  /**
   * Check if geolocation is supported (Capacitor or browser)
   * @returns {boolean} True if geolocation is supported
   */
  isSupported() {
    if (PlatformConfig.useCapacitorGeolocation) {
      // On Capacitor, assume geolocation is always available
      // (permission may still be denied, but the API exists)
      return true;
    }
    return 'geolocation' in navigator;
  }
  
  /**
   * Request location permissions (primarily for Capacitor)
   * @returns {Promise<boolean>} True if permission granted
   */
  async requestPermissions() {
    if (PlatformConfig.useCapacitorGeolocation) {
      try {
        const permissions = await Geolocation.requestPermissions();
        return permissions.location === 'granted';
      } catch (error) {
        console.warn('Failed to request location permissions:', error);
        return false;
      }
    } else {
      // For web, permission is requested automatically on getCurrentPosition
      // We can check current permission status if available
      if ('permissions' in navigator) {
        try {
          const permission = await navigator.permissions.query({ name: 'geolocation' });
          return permission.state === 'granted';
        } catch (error) {
          // Fallback: assume permission will be requested on first use
          return true;
        }
      }
      return true;
    }
  }
  
  /**
   * Check current location permission status
   * @returns {Promise<string>} Permission status: 'granted', 'denied', 'prompt', or 'unknown'
   */
  async checkPermissions() {
    if (PlatformConfig.useCapacitorGeolocation) {
      try {
        const permissions = await Geolocation.checkPermissions();
        return permissions.location || 'unknown';
      } catch (error) {
        console.warn('Failed to check location permissions:', error);
        return 'unknown';
      }
    } else {
      if ('permissions' in navigator) {
        try {
          const permission = await navigator.permissions.query({ name: 'geolocation' });
          return permission.state;
        } catch (error) {
          return 'unknown';
        }
      }
      return 'unknown';
    }
  }

  /**
   * Check if location data is valid
   * @param {Object} locationData - Location data to validate
   * @returns {boolean} True if location data is valid
   */
  isValidLocationData(locationData) {
    if (!locationData || !locationData.coordinates) {
      return false;
    }

    const { lat, lng } = locationData.coordinates;
    
    // Check latitude range: -90 to 90
    if (lat < -90 || lat > 90) {
      return false;
    }
    
    // Check longitude range: -180 to 180
    if (lng < -180 || lng > 180) {
      return false;
    }
    
    // Check valid source
    const validSources = ['gps', 'ip', 'manual'];
    if (locationData.source && !validSources.includes(locationData.source)) {
      return false;
    }
    
    // Check accuracy is positive if provided
    if (locationData.accuracy !== undefined && locationData.accuracy < 0) {
      return false;
    }
    
    return true;
  }

  /**
   * Format coordinates for display
   * @param {Object} coordinates - Coordinates object with lat and lng
   * @param {number} precision - Number of decimal places (default: 4)
   * @returns {string} Formatted coordinate string
   */
  formatCoordinates(coordinates, precision = 4) {
    if (!coordinates || coordinates.lat === undefined || coordinates.lng === undefined) {
      return 'Unknown location';
    }
    
    return `${coordinates.lat.toFixed(precision)}, ${coordinates.lng.toFixed(precision)}`;
  }
  
  /**
   * Convenience method that combines permission checking and location getting
   * @param {Object} options - Geolocation options
   * @returns {Promise<Object>} Location data with coordinates, source, accuracy, and timestamp
   */
  async getCurrentLocation(options = {}) {
    // Check if geolocation is supported
    if (!this.isSupported()) {
      throw new Error('Geolocation not supported on this platform');
    }
    
    // For mobile apps, check permissions first
    if (PlatformConfig.useCapacitorGeolocation) {
      const permissionStatus = await this.checkPermissions();
      if (permissionStatus === 'denied') {
        throw new Error('Location permission denied');
      }
      
      if (permissionStatus !== 'granted') {
        const granted = await this.requestPermissions();
        if (!granted) {
          throw new Error('Location permission denied');
        }
      }
    }
    
    // Get current position
    return await this.getCurrentPosition(options);
  }
}

// Create singleton instance
export const locationService = new LocationService();

// Export the class for testing
export { LocationService };